"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import styled from "@emotion/styled";
import { ChevronRightIcon, ExitIcon, PaperPlaneIcon, PinBottomIcon, PlusIcon, TextAlignJustifyIcon, } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { getContract } from "../../../../contract/contract.js";
import { isContractDeployed } from "../../../../utils/bytecode/is-contract-deployed.js";
import { formatNumber } from "../../../../utils/formatNumber.js";
import { CustomThemeProvider, parseTheme, useCustomTheme, } from "../../../core/design-system/CustomThemeProvider.js";
import { fontSize, iconSize, radius, spacing, } from "../../../core/design-system/index.js";
import { useChainFaucets, useChainIconUrl, useChainName, } from "../../../core/hooks/others/useChainQuery.js";
import { useActiveAccount } from "../../../core/hooks/wallets/useActiveAccount.js";
import { useActiveWallet } from "../../../core/hooks/wallets/useActiveWallet.js";
import { useActiveWalletChain } from "../../../core/hooks/wallets/useActiveWalletChain.js";
import { useDisconnect } from "../../../core/hooks/wallets/useDisconnect.js";
import { useSwitchActiveWalletChain } from "../../../core/hooks/wallets/useSwitchActiveWalletChain.js";
import { SetRootElementContext } from "../../../core/providers/RootElementContext.js";
import { hasSmartAccount } from "../../../core/utils/isSmartWallet.js";
import { useConnectedWalletDetails, useWalletInfo, } from "../../../core/utils/wallet.js";
import { WalletUIStatesProvider } from "../../providers/wallet-ui-states-provider.js";
import { ChainIcon } from "../components/ChainIcon.js";
import { CopyIcon } from "../components/CopyIcon.js";
import { IconContainer } from "../components/IconContainer.js";
import { Modal } from "../components/Modal.js";
import { Skeleton } from "../components/Skeleton.js";
import { Spacer } from "../components/Spacer.js";
import { Spinner } from "../components/Spinner.js";
import { ToolTip } from "../components/Tooltip.js";
import { WalletImage } from "../components/WalletImage.js";
import { Container, Line } from "../components/basic.js";
import { Button, IconButton } from "../components/buttons.js";
import { Link, Text } from "../components/text.js";
import { fadeInAnimation } from "../design-system/animations.js";
import { StyledButton } from "../design-system/elements.js";
import { Blobbie } from "./Blobbie.js";
import { MenuButton, MenuLink } from "./MenuButton.js";
import { ScreenSetupContext, useSetupScreen } from "./Modal/screen.js";
import { NetworkSelectorContent, } from "./NetworkSelector.js";
import { TransactionsScreen } from "./TransactionsScreen.js";
import { onModalUnmount } from "./constants.js";
import { CoinsIcon } from "./icons/CoinsIcon.js";
import { FundsIcon } from "./icons/FundsIcon.js";
import { OutlineWalletIcon } from "./icons/OutlineWalletIcon.js";
import { getConnectLocale } from "./locale/getConnectLocale.js";
import { LazyBuyScreen } from "./screens/Buy/LazyBuyScreen.js";
import { WalletManagerScreen } from "./screens/Details/WalletManagerScreen.js";
import { LinkProfileScreen } from "./screens/LinkProfileScreen.js";
import { LinkedProfilesScreen } from "./screens/LinkedProfilesScreen.js";
import { ManageWalletScreen } from "./screens/ManageWalletScreen.js";
import { PrivateKey } from "./screens/PrivateKey.js";
import { ReceiveFunds } from "./screens/ReceiveFunds.js";
import { SendFunds } from "./screens/SendFunds.js";
import { ViewAssets } from "./screens/ViewAssets.js";
import { ViewNFTs } from "./screens/ViewNFTs.js";
import { ViewTokens } from "./screens/ViewTokens.js";
import { WalletConnectReceiverScreen } from "./screens/WalletConnectReceiverScreen.js";
const TW_CONNECTED_WALLET = "tw-connected-wallet";
const LocalhostChainId = 1337;
/**
 * @internal
 */
export const ConnectedWalletDetails = (props) => {
    const { connectLocale: locale, client } = props;
    const setRootEl = useContext(SetRootElementContext);
    const activeAccount = useActiveAccount();
    const walletChain = useActiveWalletChain();
    const { pfp, name, balanceQuery } = useConnectedWalletDetails(client, walletChain, activeAccount, props.detailsButton?.displayBalanceToken);
    function closeModal() {
        setRootEl(null);
    }
    function openModal() {
        setRootEl(_jsx(DetailsModal, { client: client, locale: locale, detailsModal: props.detailsModal, theme: props.theme, supportedTokens: props.supportedTokens, supportedNFTs: props.supportedNFTs, closeModal: closeModal, onDisconnect: props.onDisconnect, chains: props.chains, displayBalanceToken: props.detailsButton?.displayBalanceToken, connectOptions: props.connectOptions }));
    }
    const isNetworkMismatch = props.chain && walletChain && walletChain.id !== props.chain.id;
    if (props.detailsButton?.render) {
        return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: ok
        _jsx("div", { onClick: openModal, children: _jsx(props.detailsButton.render, {}) }));
    }
    if (props.chain && isNetworkMismatch) {
        return (_jsx(SwitchNetworkButton, { style: props.switchButton?.style, className: props.switchButton?.className, switchNetworkBtnTitle: props.switchButton?.label, targetChain: props.chain, connectLocale: locale }));
    }
    const avatarSrc = props.detailsButton?.connectedAccountAvatarUrl || pfp;
    return (_jsxs(WalletInfoButton, { type: "button", className: `${TW_CONNECTED_WALLET} ${props.detailsButton?.className || ""}`, style: props.detailsButton?.style, "data-test": "connected-wallet-details", onClick: openModal, children: [_jsx(Container, { style: {
                    borderRadius: "100%",
                    overflow: "hidden",
                    width: "35px",
                    height: "35px",
                }, children: avatarSrc ? (_jsx("img", { alt: "", src: avatarSrc, style: {
                        width: "100%",
                        height: "100%",
                    } })) : (activeAccount && _jsx(Blobbie, { address: activeAccount.address, size: 35 })) }), _jsxs(Container, { flex: "column", gap: "4xs", style: {
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    borderRadius: `0 ${radius.md} ${radius.md} 0`,
                }, children: [_jsx(Text, { size: "xs", color: "primaryText", weight: 500, className: `${TW_CONNECTED_WALLET}__address`, children: props.detailsButton?.connectedAccountName ?? name }), balanceQuery.data ? (_jsxs(Text, { className: `${TW_CONNECTED_WALLET}__balance`, size: "xs", color: "secondaryText", weight: 400, children: [formatBalanceOnButton(Number(balanceQuery.data.displayValue)), " ", balanceQuery.data.symbol] })) : (_jsx(Skeleton, { height: fontSize.xs, width: "70px" }))] })] }));
};
function DetailsModal(props) {
    const [screen, setScreen] = useState("main");
    const { disconnect } = useDisconnect();
    const [isOpen, setIsOpen] = useState(true);
    const { client, locale } = props;
    const walletChain = useActiveWalletChain();
    const activeAccount = useActiveAccount();
    const { pfp, name, balanceQuery } = useConnectedWalletDetails(client, walletChain, activeAccount, props.displayBalanceToken);
    const theme = parseTheme(props.theme);
    const activeWallet = useActiveWallet();
    const chainIconQuery = useChainIconUrl(walletChain);
    const chainNameQuery = useChainName(walletChain);
    const chainFaucetsQuery = useChainFaucets(walletChain);
    const disableSwitchChain = !activeWallet?.switchChain;
    const screenSetup = useSetupScreen({
        size: "compact",
        welcomeScreen: undefined,
        wallets: activeWallet ? [activeWallet] : [],
    });
    function closeModal() {
        setIsOpen(false);
        onModalUnmount(() => {
            props.closeModal();
        });
    }
    function handleDisconnect(info) {
        setIsOpen(false);
        props.closeModal();
        props.onDisconnect(info);
    }
    const networkSwitcherButton = (_jsxs(MenuButton, { type: "button", disabled: disableSwitchChain, onClick: () => {
            setScreen("network-switcher");
        }, "data-variant": "primary", children: [_jsx("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                }, children: !chainIconQuery.isLoading ? (_jsx(ChainIcon, { chainIconUrl: chainIconQuery.url, size: iconSize.md, active: true, client: client })) : (_jsx(Skeleton, { height: `${iconSize.md}px`, width: `${iconSize.md}px` })) }), chainNameQuery.isLoading ? (_jsx(Skeleton, { height: "16px", width: "150px" })) : (_jsxs(Text, { color: "primaryText", size: "md", multiline: true, children: [chainNameQuery.name || `Unknown chain #${walletChain?.id}`, _jsxs(Text, { color: "secondaryText", size: "xs", children: [balanceQuery.data ? (formatNumber(Number(balanceQuery.data.displayValue), 5)) : (_jsx(Skeleton, { height: "1em", width: "100px" })), " ", balanceQuery.data?.symbol] })] })), _jsx(StyledChevronRightIcon, { width: iconSize.sm, height: iconSize.sm, style: {
                    flexShrink: 0,
                    marginLeft: "auto",
                } })] }));
    const avatarSrc = props.detailsModal?.connectedAccountAvatarUrl ?? pfp;
    const { hideSendFunds, hideReceiveFunds, hideBuyFunds } = props.detailsModal || {};
    const hideAllButtons = hideSendFunds && hideReceiveFunds && hideBuyFunds;
    const avatarContent = (_jsxs(Container, { style: {
            position: "relative",
            height: `${iconSize.xl}px`,
            width: `${iconSize.xl}px`,
        }, children: [_jsx(Container, { style: {
                    width: "100%",
                    height: "100%",
                    borderRadius: "100%",
                    overflow: "hidden",
                }, children: avatarSrc ? (_jsx("img", { src: avatarSrc, style: {
                        width: iconSize.xxl,
                        height: iconSize.xxl,
                    }, alt: "" })) : (activeAccount && (_jsx(Blobbie, { address: activeAccount.address, size: Number(iconSize.xxl) }))) }), !props.detailsModal?.hideSwitchWallet ? (_jsx(Container, { style: {
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                }, children: _jsx(IconContainer, { style: {
                        background: theme.colors.modalBg,
                    }, padding: "4px", children: activeWallet && (_jsx(WalletImage, { style: { borderRadius: 0 }, id: activeWallet.id, client: client, size: "12" })) }) })) : null] }));
    let content = (_jsxs("div", { children: [_jsx(Spacer, { y: "xs" }), _jsxs(Container, { px: "lg", gap: "sm", flex: "row", center: "y", style: {
                    paddingTop: spacing.lg,
                    paddingBottom: hideAllButtons ? spacing.md : spacing.lg,
                }, children: [props.detailsModal?.hideSwitchWallet ? (avatarContent) : (_jsx(ToolTip, { tip: "Switch wallet", children: _jsx("div", { style: {
                                cursor: "pointer",
                            }, onKeyDown: (e) => {
                                if (e.key === "w") {
                                    setScreen("wallet-manager");
                                }
                            }, onClick: () => {
                                setScreen("wallet-manager");
                            }, children: avatarContent }) })), _jsxs(Container, { flex: "column", gap: "3xs", children: [_jsxs("div", { style: {
                                    display: "flex",
                                    gap: spacing.xxs,
                                    alignItems: "center",
                                }, children: [_jsx(Text, { color: "primaryText", weight: 500, size: "md", children: props.detailsModal?.connectedAccountName ?? name }), _jsx(IconButton, { children: _jsx(CopyIcon, { text: activeAccount?.address || "", tip: locale.copyAddress }) })] }), _jsx(InAppWalletUserInfo, { client: client, locale: locale })] })] }), !hideAllButtons && (_jsxs(_Fragment, { children: [_jsx(Container, { px: "lg", children: _jsxs(Container, { style: {
                                display: "flex",
                                gap: spacing.xs,
                            }, children: [!hideSendFunds && (_jsxs(Button, { variant: "outline", style: {
                                        fontSize: fontSize.sm,
                                        display: "flex",
                                        gap: spacing.xs,
                                        alignItems: "center",
                                        padding: spacing.sm,
                                        flex: 1,
                                    }, onClick: () => {
                                        setScreen("send");
                                    }, children: [_jsx(Container, { color: "secondaryText", flex: "row", center: "both", children: _jsx(PaperPlaneIcon, { width: iconSize.sm, height: iconSize.sm, style: {
                                                    transform: "translateY(-10%) rotate(-45deg) ",
                                                } }) }), locale.send] })), !hideReceiveFunds && (_jsxs(Button, { variant: "outline", style: {
                                        fontSize: fontSize.sm,
                                        display: "flex",
                                        gap: spacing.xs,
                                        alignItems: "center",
                                        padding: spacing.sm,
                                        flex: 1,
                                    }, onClick: () => {
                                        setScreen("receive");
                                    }, children: [_jsxs(Container, { color: "secondaryText", flex: "row", center: "both", children: [_jsx(PinBottomIcon, { width: iconSize.sm, height: iconSize.sm }), " "] }), locale.receive, " "] })), !hideBuyFunds && (_jsxs(Button, { variant: "outline", style: {
                                        fontSize: fontSize.sm,
                                        display: "flex",
                                        gap: spacing.xs,
                                        alignItems: "center",
                                        padding: spacing.sm,
                                        flex: 1,
                                    }, onClick: () => {
                                        setScreen("buy");
                                    }, children: [_jsx(Container, { color: "secondaryText", flex: "row", center: "both", children: _jsx(PlusIcon, { width: iconSize.sm, height: iconSize.sm }) }), locale.buy] }))] }) }), _jsx(Spacer, { y: "md" })] })), _jsxs(Container, { px: "md", children: [_jsxs(Container, { flex: "column", style: {
                            gap: "1px",
                        }, children: [networkSwitcherButton, _jsxs(MenuButton, { onClick: () => {
                                    setScreen("transactions");
                                }, style: {
                                    fontSize: fontSize.sm,
                                }, children: [_jsx(TextAlignJustifyIcon, { width: iconSize.md, height: iconSize.md }), _jsx(Container, { flex: "row", gap: "xs", center: "y", children: _jsx(Text, { color: "primaryText", children: locale.transactions }) })] }), _jsxs(MenuButton, { onClick: () => {
                                    setScreen("view-assets");
                                }, style: {
                                    fontSize: fontSize.sm,
                                }, children: [_jsx(CoinsIcon, { size: iconSize.md }), _jsx(Text, { color: "primaryText", children: props.supportedNFTs
                                            ? locale.viewFunds.viewAssets
                                            : locale.viewFunds.title })] }), _jsxs(MenuButton, { onClick: () => {
                                    setScreen("manage-wallet");
                                }, style: {
                                    fontSize: fontSize.sm,
                                }, children: [_jsx(OutlineWalletIcon, { size: iconSize.md }), _jsx(Text, { color: "primaryText", children: props.locale.manageWallet.title })] }), (props.detailsModal?.showTestnetFaucet ?? false) &&
                                (chainFaucetsQuery.faucets.length > 0 ||
                                    walletChain?.id === LocalhostChainId) && (_jsxs(MenuLink, { href: chainFaucetsQuery.faucets ? chainFaucetsQuery.faucets[0] : "#", target: "_blank", as: "a", style: {
                                    textDecoration: "none",
                                    color: "inherit",
                                }, children: [_jsx(Container, { flex: "row", center: "both", color: "secondaryText", children: _jsx(FundsIcon, { size: iconSize.md }) }), locale.requestTestnetFunds] })), props.detailsModal?.footer && (_jsx(props.detailsModal.footer, { close: closeModal }))] }), _jsx(Spacer, { y: "md" })] }), props.detailsModal?.hideDisconnect !== true && (_jsxs(Container, { children: [_jsx(Line, {}), _jsx(Spacer, { y: "sm" }), _jsx(Container, { px: "md", children: _jsxs(MenuButton, { "data-variant": "danger", type: "button", onClick: () => {
                                if (activeWallet && activeAccount) {
                                    disconnect(activeWallet);
                                    handleDisconnect({
                                        account: activeAccount,
                                        wallet: activeWallet,
                                    });
                                }
                            }, children: [_jsx(ExitIcon, { width: iconSize.md, height: iconSize.md }), _jsx(Text, { color: "primaryText", children: locale.disconnectWallet })] }) }), _jsx(Spacer, { y: "sm" })] }))] }));
    if (screen === "transactions") {
        content = (_jsx(TransactionsScreen, { title: locale.buy, onBack: () => setScreen("main"), closeModal: closeModal, locale: locale, setScreen: setScreen, client: client }));
    }
    if (screen === "wallet-manager" &&
        activeAccount &&
        walletChain &&
        activeWallet) {
        content = (_jsx(WalletManagerScreen, { onBack: () => setScreen("main"), accountAbstraction: props.connectOptions?.accountAbstraction, appMetadata: props.connectOptions?.appMetadata, chain: props.connectOptions?.chain, chains: props.connectOptions?.chains, client: client, connectLocale: locale, recommendedWallets: props.connectOptions?.recommendedWallets, showAllWallets: !!props.connectOptions?.showAllWallets, walletConnect: props.connectOptions?.walletConnect, wallets: props.connectOptions?.wallets, activeAccount: activeAccount, activeChain: walletChain, activeWallet: activeWallet }));
    }
    if (screen === "network-switcher") {
        content = (_jsx(NetworkSelectorContent
        // add currently connected chain to the list of chains if it's not already in the list
        , { 
            // add currently connected chain to the list of chains if it's not already in the list
            chains: walletChain &&
                props.chains.find((c) => c.id === walletChain.id) === undefined
                ? [walletChain, ...props.chains]
                : props.chains, closeModal: closeModal, networkSelector: props.detailsModal?.networkSelector, onBack: () => {
                setScreen("main");
            }, connectLocale: locale, client: client }));
    }
    else if (screen === "view-assets") {
        if (props.supportedNFTs) {
            content = (_jsx(ViewAssets, { supportedTokens: props.supportedTokens, supportedNFTs: props.supportedNFTs, onBack: () => {
                    setScreen("main");
                }, theme: props.theme, setScreen: setScreen, client: client, connectLocale: locale }));
        }
        else {
            // Always show tokens (has the native token at least)
            content = (_jsx(ViewTokens, { supportedTokens: props.supportedTokens, onBack: () => {
                    setScreen("main");
                }, client: client, connectLocale: locale }));
        }
    }
    else if (screen === "view-nfts") {
        content = (_jsx(ViewNFTs, { theme: props.theme, supportedNFTs: props.supportedNFTs, onBack: () => {
                setScreen("main");
            }, client: client, connectLocale: locale }));
    }
    else if (screen === "view-tokens") {
        content = (_jsx(ViewTokens, { supportedTokens: props.supportedTokens, onBack: () => {
                setScreen("main");
            }, client: client, connectLocale: locale }));
    }
    else if (screen === "private-key") {
        content = (_jsx(PrivateKey, { theme: props.theme, onBack: () => {
                setScreen("manage-wallet");
            }, wallet: activeWallet, client: client }));
    }
    else if (screen === "manage-wallet") {
        content = (_jsx(ManageWalletScreen, { onBack: () => {
                setScreen("main");
            }, locale: locale, closeModal: closeModal, client: client, setScreen: setScreen }));
    }
    else if (screen === "wallet-connect-receiver") {
        content = (_jsx(WalletConnectReceiverScreen, { onBack: () => {
                setScreen("manage-wallet");
            }, chains: props.chains, client: client }));
    }
    else if (screen === "linked-profiles") {
        content = (_jsx(LinkedProfilesScreen, { onBack: () => setScreen("manage-wallet"), client: client, locale: locale, setScreen: setScreen }));
    }
    else if (screen === "link-profile") {
        content = (_jsx(LinkProfileScreen, { onBack: () => {
                setScreen("linked-profiles");
            }, client: client, locale: locale, walletConnect: props.connectOptions?.walletConnect }));
    }
    // send funds
    else if (screen === "send") {
        content = (_jsx(SendFunds, { supportedTokens: props.supportedTokens, onBack: () => {
                setScreen("main");
            }, client: client, connectLocale: locale }));
    }
    // receive funds
    else if (screen === "receive") {
        content = (_jsx(ReceiveFunds, { walletId: activeWallet?.id, onBack: () => {
                setScreen("main");
            }, client: client, connectLocale: locale }));
    }
    // thirdweb pay
    else if (screen === "buy") {
        content = (_jsx(LazyBuyScreen, { title: locale.buy, isEmbed: false, client: client, onBack: () => setScreen("main"), supportedTokens: props.supportedTokens, connectLocale: locale, payOptions: props.detailsModal?.payOptions || {
                mode: "fund_wallet",
            }, theme: typeof props.theme === "string" ? props.theme : props.theme.type, onDone: closeModal, connectOptions: undefined }));
    }
    return (_jsx(CustomThemeProvider, { theme: props.theme, children: _jsx(WalletUIStatesProvider, { theme: props.theme, isOpen: false, children: _jsx(ScreenSetupContext.Provider, { value: screenSetup, children: _jsx(Modal, { size: "compact", open: isOpen, setOpen: (_open) => {
                        if (!_open) {
                            closeModal();
                        }
                    }, children: content }) }) }) }));
}
function formatBalanceOnButton(num) {
    return formatNumber(num, num < 1 ? 5 : 4);
}
const WalletInfoButton = /* @__PURE__ */ StyledButton((_) => {
    const theme = useCustomTheme();
    return {
        all: "unset",
        background: theme.colors.connectedButtonBg,
        overflow: "hidden",
        borderRadius: radius.md,
        cursor: "pointer",
        display: "inline-flex",
        gap: spacing.xs,
        padding: spacing.xs,
        alignItems: "center",
        minWidth: "165px",
        height: "50px",
        boxSizing: "border-box",
        border: `1px solid ${theme.colors.borderColor}`,
        WebkitTapHighlightColor: "transparent",
        lineHeight: "normal",
        animation: `${fadeInAnimation} 300ms ease`,
        "&:hover": {
            transition: "background 250ms ease",
            background: theme.colors.connectedButtonBgHover,
        },
    };
});
const StyledChevronRightIcon = /* @__PURE__ */ styled(
/* @__PURE__ */ ChevronRightIcon)(() => {
    const theme = useCustomTheme();
    return {
        color: theme.colors.secondaryText,
    };
});
function ConnectedToSmartWallet(props) {
    const activeAccount = useActiveAccount();
    const activeWallet = useActiveWallet();
    const isSmartWallet = hasSmartAccount(activeWallet);
    const chain = useActiveWalletChain();
    const { client, connectLocale: locale } = props;
    const [isSmartWalletDeployed, setIsSmartWalletDeployed] = useState(false);
    useEffect(() => {
        if (activeAccount && isSmartWallet && activeAccount.address && chain) {
            const contract = getContract({
                address: activeAccount.address,
                chain,
                client,
            });
            isContractDeployed(contract).then((isDeployed) => {
                setIsSmartWalletDeployed(isDeployed);
            });
        }
        else {
            setIsSmartWalletDeployed(false);
        }
    }, [activeAccount, chain, client, isSmartWallet]);
    const content = (_jsx(Container, { flex: "row", gap: "3xs", center: "y", children: _jsx(Text, { size: "xs", weight: 400, color: "secondaryText", children: locale.connectedToSmartWallet }) }));
    if (chain && activeAccount && isSmartWallet) {
        return (_jsx(_Fragment, { children: isSmartWalletDeployed ? (_jsx(Link, { color: "secondaryText", hoverColor: "primaryText", href: `https://thirdweb.com/${chain.id}/${activeAccount.address}/account`, target: "_blank", size: "sm", children: content })) : (_jsxs(Text, { size: "sm", children: [" ", content] })) }));
    }
    return null;
}
function InAppWalletUserInfo(props) {
    const { client, locale } = props;
    const account = useActiveAccount();
    const activeWallet = useActiveWallet();
    const { data: walletInfo } = useWalletInfo(activeWallet?.id);
    const isSmartWallet = hasSmartAccount(activeWallet);
    const userInfoQuery = useQuery({
        queryKey: ["in-app-wallet-user", client, account?.address],
        queryFn: async () => {
            const { getUserEmail, getUserPhoneNumber } = await import("../../../../wallets/in-app/web/lib/auth/index.js");
            const [email, phone] = await Promise.all([
                getUserEmail({
                    client: client,
                }),
                getUserPhoneNumber({
                    client: client,
                }),
            ]);
            return email || phone || null;
        },
        enabled: !isSmartWallet,
    });
    if (isSmartWallet) {
        return _jsx(ConnectedToSmartWallet, { client: client, connectLocale: locale });
    }
    if (userInfoQuery.data || walletInfo) {
        return (_jsx(Text, { size: "xs", weight: 400, children: userInfoQuery.data || walletInfo?.name }));
    }
    return _jsx(Skeleton, { width: "50px", height: "10px" });
}
/**
 * @internal
 */
function SwitchNetworkButton(props) {
    const switchChain = useSwitchActiveWalletChain();
    const [switching, setSwitching] = useState(false);
    const locale = props.connectLocale;
    const switchNetworkBtnTitle = props.switchNetworkBtnTitle ?? locale.switchNetwork;
    return (_jsx(Button, { className: `tw-connect-wallet--switch-network ${props.className || ""}`, variant: "primary", type: "button", "data-is-loading": switching, "data-test": "switch-network-button", disabled: switching, onClick: async () => {
            setSwitching(true);
            try {
                await switchChain(props.targetChain);
            }
            catch (e) {
                console.error(e);
            }
            setSwitching(false);
        }, style: {
            minWidth: "140px",
            ...props.style,
        }, "aria-label": switching ? locale.switchingNetwork : undefined, children: switching ? (_jsx(Spinner, { size: "sm", color: "primaryButtonText" })) : (switchNetworkBtnTitle) }));
}
/**
 * Hook to open the Wallet Details Modal that shows various information about the connected wallet and allows users to perform various actions like sending funds, receiving funds, switching networks, Buying tokens, etc.
 *
 * @example
 * ```tsx
 * import { createThirdwebClient } from "thirdweb";
 * import { useWalletDetailsModal } from "thirdweb/react";
 *
 * const client = createThirdwebClient({
 *  clientId: "<your_client_id>",
 * });
 *
 * function Example() {
 *   const detailsModal = useWalletDetailsModal();
 *
 *   function handleClick() {
 *      detailsModal.open({ client, theme: 'light' });
 *   }
 *
 *   return <button onClick={handleClick}> Show Wallet Details </button>
 * }
 * ```
 * @wallet
 */
export function useWalletDetailsModal() {
    const account = useActiveAccount();
    const setRootEl = useContext(SetRootElementContext);
    function closeModal() {
        setRootEl(null);
    }
    function openModal(props) {
        if (!account) {
            throw new Error("Wallet is not connected.");
        }
        getConnectLocale(props.locale || "en_US")
            .then((locale) => {
            setRootEl(_jsx(DetailsModal, { client: props.client, locale: locale, detailsModal: {
                    footer: props.footer,
                    hideDisconnect: props.hideDisconnect,
                    hideSwitchWallet: props.hideSwitchWallet,
                    networkSelector: props.networkSelector,
                    payOptions: props.payOptions,
                    showTestnetFaucet: props.showTestnetFaucet,
                    connectedAccountName: props.connectedAccountName,
                    connectedAccountAvatarUrl: props.connectedAccountAvatarUrl,
                    hideBuyFunds: props.hideBuyFunds,
                    hideReceiveFunds: props.hideReceiveFunds,
                    hideSendFunds: props.hideSendFunds,
                }, displayBalanceToken: props.displayBalanceToken, theme: props.theme || "dark", supportedTokens: props.supportedTokens, supportedNFTs: props.supportedNFTs, closeModal: closeModal, onDisconnect: (info) => {
                    props.onDisconnect?.(info);
                    closeModal();
                }, chains: props.chains || [], connectOptions: props.connectOptions }));
        })
            .catch(() => {
            closeModal();
        });
    }
    return {
        open: openModal,
    };
}
//# sourceMappingURL=Details.js.map